import { useSelector } from 'react-redux';

import { AnalyticsMetricCurrency } from './AnalyticsMetricCurrency';
import { AnalyticsMetricAmountAndPercentage } from './AnalyticsMetricAmountAndPercentage';

import { getMetricsFromData } from './utils';

import styles from './AnalyticsMetrics.module.scss';
import { REPORT_TYPES } from '../../tables/reports/consts';
import { getRate } from '../../../utils/analytics';

export function AnalyticsMetrics() {
  const {
    currentCurrency,
    ui: {
      loading: {
        [REPORT_TYPES.SALES_BY_DAY]: dataLoading,
        prevData: prevDataLoading,
        countries: countriesLoading,
        retailers: retailersLoading,
      },
    },
    additionalData: {
      prevData: { data: prevData },
      [REPORT_TYPES.SALES_BY_DAY]: { data },
    },
  } = useSelector((state) => state.analytics);

  let dataValues, prevDataValues;
  let cancellationsValue, cancellationsRate, prevCancellationValue, prevCancellationRate;
  let returnsValue, returnsRate, prevReturnsValue, prevReturnsRate;

  const loading = dataLoading || prevDataLoading || countriesLoading || retailersLoading || !data;
  if (!loading) {
    dataValues = getMetricsFromData(data, currentCurrency);
    prevDataValues = getMetricsFromData(prevData || [], currentCurrency);

    cancellationsValue = dataValues.gmv?.cancelledOrders;
    cancellationsRate = getRate(dataValues.unitCount?.cancelledOrders, dataValues.unitCount?.allOrders);
    prevCancellationValue = prevDataValues.gmv?.cancelledOrders;
    prevCancellationRate = getRate(prevDataValues.unitCount?.cancelledOrders, prevDataValues.unitCount?.allOrders);

    returnsValue = dataValues.gmv?.returns;
    returnsRate = getRate(dataValues.unitCount?.returns, dataValues.unitCount?.allOrders);
    prevReturnsValue = prevDataValues.gmv?.returns;
    prevReturnsRate = getRate(prevDataValues.unitCount?.returns, prevDataValues.unitCount?.allOrders);
  }

  return (
    <div className={styles.analyticsMetrics}>
      <AnalyticsMetricCurrency
        title="Total Sales"
        tooltip={
          <>
            <p>The total Sales value, including Gross Merchandise Value (GMV) and Gross Wholesale Value (GWV).</p>
            <p>
              <strong>GMV:</strong> The total value of sold products, excluding shipping and taxes.
            </p>
            <p>
              <strong>GWV:</strong> The wholesale price of the products
            </p>
          </>
        }
        value={dataValues?.gmv?.allOrders}
        currency={currentCurrency}
        prevValue={prevDataValues?.gmv?.allOrders}
        loading={loading}
      />
      <AnalyticsMetricCurrency
        title="Total net sales"
        tooltip="The total sales value after deducting cancellations and refunds"
        value={dataValues?.gmv?.netSales}
        currency={currentCurrency}
        prevValue={prevDataValues?.gmv?.netSales}
        loading={loading}
      />
      <AnalyticsMetricCurrency
        title="AOV"
        tooltip="The Average Order Value of all orders within the selected time period"
        value={dataValues?.totalOrdersCount ? dataValues?.gmv?.allOrders / dataValues?.totalOrdersCount : 0}
        currency={currentCurrency}
        prevValue={
          prevDataValues?.totalOrdersCount ? prevDataValues?.gmv?.allOrders / prevDataValues?.totalOrdersCount : 0
        }
        loading={loading}
        roundValue
      />
      <AnalyticsMetricAmountAndPercentage
        title="Cancellations"
        currency={currentCurrency}
        value={cancellationsValue}
        valuePercentage={cancellationsRate}
        prevValue={prevCancellationValue}
        prevValuePercentage={prevCancellationRate}
        isOppositeTrend={true}
        loading={loading}
        tooltip={
          <>
            <p>
              <strong>Cancellation value:</strong> The total monetary value of ordered products that were not delivered
            </p>
            <p>
              <strong>Cancellation Rate:</strong> The percentage of ordered units that were not delivered out of the
              total sold units
            </p>
            <p>
              <strong>Cancellation Trend:</strong> The trend of cancellation value compared to the previous time period
            </p>
          </>
        }
      />
      <AnalyticsMetricAmountAndPercentage
        title="Returns"
        currency={currentCurrency}
        value={returnsValue}
        valuePercentage={returnsRate}
        prevValue={prevReturnsValue}
        prevValuePercentage={prevReturnsRate}
        isOppositeTrend={true}
        loading={loading}
        tooltip={
          <>
            <p>
              <strong>Returns value:</strong> The total monetary value of ordered products that were returned
            </p>
            <p>
              <strong>Returns Rate:</strong> The percentage of ordered units that were returned out of the total sold
            </p>
            <p>
              <strong>Returns Trend:</strong> The trend of returns value compared to the previous time period
            </p>
          </>
        }
      />
    </div>
  );
}
